import { useState } from 'react';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

interface CouponRow {
  id: string;
  code: string;
  description: string;
  rewardCoins: number;
  maxRedemptions: number | null;
  redemptionCount: number;
  minRechargePaise: number | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
}

interface CouponForm {
  code: string;
  description?: string;
  rewardCoins: number;
  maxRedemptions?: number | null;
  minRechargePaise?: number | null;
  /** YYYY-MM-DD (blank = never). */
  expiresAt?: string;
  isActive: boolean;
}

/** Admin: coin-reward coupon CRUD. Redeeming creates a claimable reward. */
export function CouponsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CouponRow | null>(null);
  const [form] = Form.useForm<CouponForm>();

  const coupons = useQuery({
    queryKey: ['admin', 'coupons'],
    queryFn: async () => (await api.get<CouponRow[]>('/admin/coupons')).data,
  });

  const save = useMutation({
    mutationFn: async (v: CouponForm) => {
      const d = v.expiresAt ? new Date(v.expiresAt) : null;
      const payload = {
        code: v.code,
        description: v.description ?? '',
        rewardCoins: v.rewardCoins,
        maxRedemptions: v.maxRedemptions ?? null,
        minRechargePaise: v.minRechargePaise ?? null,
        expiresAt: d && !Number.isNaN(d.getTime()) ? d.toISOString() : null,
        isActive: v.isActive,
      };
      if (editing) return (await api.patch(`/admin/coupons/${editing.id}`, payload)).data;
      return (await api.post('/admin/coupons', payload)).data;
    },
    onSuccess: () => {
      message.success('Coupon saved.');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed',
      ),
  });

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, rewardCoins: 100 });
    setOpen(true);
  };
  const openEdit = (c: CouponRow) => {
    setEditing(c);
    form.setFieldsValue({
      code: c.code,
      description: c.description,
      rewardCoins: c.rewardCoins,
      maxRedemptions: c.maxRedemptions,
      minRechargePaise: c.minRechargePaise,
      expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : undefined,
      isActive: c.isActive,
    });
    setOpen(true);
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>Coupons</Typography.Title>
      <Typography.Paragraph type="secondary">
        Coin-reward coupons. Redeeming creates a claimable reward in the user&apos;s Reward Center.
      </Typography.Paragraph>
      <Can permission="coupons.create">
        <Button type="primary" onClick={openNew} style={{ marginBottom: 16 }}>
          New coupon
        </Button>
      </Can>
      <Card>
        <Table<CouponRow>
          rowKey="id"
          dataSource={coupons.data ?? []}
          loading={coupons.isLoading}
          size="small"
          columns={[
            { title: 'Code', dataIndex: 'code', key: 'code' },
            { title: 'Coins', dataIndex: 'rewardCoins', key: 'rewardCoins' },
            {
              title: 'Used',
              key: 'used',
              render: (_: unknown, c) =>
                `${c.redemptionCount}${c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}`,
            },
            {
              title: 'Min recharge',
              dataIndex: 'minRechargePaise',
              key: 'minRechargePaise',
              render: (v: number | null) => (v ? `₹${v / 100}` : '—'),
            },
            {
              title: 'Expires',
              dataIndex: 'expiresAt',
              key: 'expiresAt',
              render: (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—'),
            },
            {
              title: 'Active',
              dataIndex: 'isActive',
              key: 'isActive',
              render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
            },
            {
              title: '',
              key: 'actions',
              render: (_: unknown, c) => (
                <Can permission="coupons.edit">
                  <Button size="small" onClick={() => openEdit(c)}>
                    Edit
                  </Button>
                </Can>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? 'Edit coupon' : 'New coupon'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
      >
        <Form<CouponForm> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true, min: 3, max: 40 }]}>
            <Input placeholder="WELCOME100" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Welcome bonus" />
          </Form.Item>
          <Form.Item name="rewardCoins" label="Reward coins" rules={[{ required: true, type: 'number', min: 1 }]}>
            <InputNumber min={1} max={1000000} style={{ width: '100%' }} addonAfter="coins" />
          </Form.Item>
          <Form.Item name="maxRedemptions" label="Max redemptions (blank = unlimited)">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="minRechargePaise"
            label="Minimum recharge (paise, blank = none)"
            tooltip="₹99 = 9900. Requires the user to have recharged at least this amount."
          >
            <InputNumber min={0} style={{ width: '100%' }} addonAfter="paise" />
          </Form.Item>
          <Form.Item name="expiresAt" label="Expiry (YYYY-MM-DD, blank = never)">
            <Input placeholder="2026-12-31" />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
